import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Ability, Select } from '../../../components';
import { Goal, Project, Change } from '../../../models';
import { RootState, useAppDispatch, useAppSelector } from '../../../redux';
import { loadGoals, loadGoal } from '../../../redux';
import { Icon, Button, Reporting, Tooltip, FieldHelp, ChangeListItem } from '../../../components';
import { Progress } from 'flowbite-react';
import moment from 'moment';
import { formatMomentDate } from '../../../locales';

function GoalView() {
    const { t, i18n } = useTranslation();
    const dispatch    = useAppDispatch();
    const navigate    = useNavigate();

    const { id } = useParams();

    const { goal, goals, loadingOne } = useAppSelector((state: RootState) => state.goals);

    useEffect(() => {
        dispatch(loadGoals());
    }, [dispatch]);

    useEffect(() => {
        if (id && (loadingOne !== 'pending'))
            dispatch(loadGoal(id));
    }, [dispatch, id]);

    const goToGoal = (goalId: string | number) => {
        return navigate(`/goals/${goalId}`);
    };

    if (!goal)
        return null;

    const renderProgressBar = (progression: number) => (
        <Tooltip content={progression + '%'} placement="top">
            <div>
                <Progress
                    progress={progression}
                    size="lg"
                    color={ progression === 100 ? 'green' : (progression > 20 ? 'cyan' : 'yellow')}
                />
            </div>
        </Tooltip>
    );

    const goalProgression = (goal: Goal, changes?: Change[]) => {
        return renderProgressBar(Math.round(goal?.progression(changes)));
    };

    const timeProgression = (goal: Goal) => {
        const nbDays     = goal.stopAt.diff(goal.startAt, 'days');
        const burnedDays = moment().diff(goal.startAt, 'days');

        const progression = Math.round(burnedDays / nbDays * 100);

        return (
            <Tooltip content={progression + '%'} placement="top">
                <div>
                    <Progress
                        progress={progression}
                        size="lg"
                        color={ progression === 100 ? 'green' : (progression > 20 ? 'cyan' : 'yellow')}
                    />
                </div>
            </Tooltip>
        );
    };

    const isLoading = ((loadingOne + '') === 'pending');

    document.title = `Yoda - ${t('changes.goal')} ${goal.name}`;

    return (
        <Ability can="goal:read" redirect="/">
            <div className="GoalView print:font-netox">
                <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-0 sm:px-6 lg:px-8 print:hidden">
                    <div className="mb-2 mx-auto lg:flex lg:items-center lg:justify-between">
                        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0">
                            <h2 className="flex items-center text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                                <Icon type="goal" className="inline" size={8} />
                                <Select
                                    onChange={goToGoal}
                                    placeholder={goal?.name}
                                    options={goals.filter((p: Goal) => p._id !== goal?._id).map((goal: Goal) => ({
                                        value: goal._id,
                                        label: goal.name,
                                        className: 'text-xl font-bold',
                                        leftContent: (
                                            <Icon type="goal" className="inline ml-3 border rounded" size={6} />
                                        )
                                    }))}
                                    inline
                                    className=""
                                    optionsWidth={'w-[30rem]'}
                                    optionsHeight={'max-h-96'}
                                    selectedClassName="text-xl font-bold text-gray-900 dark:text-gray-100"
                                    optionsClassName="w-[29rem] text-sm font-medium py-2 pl-10 truncate"
                                />
                            </h2>
                        </div>
                        <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row lg:ml-4 mt-2 lg:mt-0">
                            <Ability can="goal:edit">
                                <span className="sm:ml-3">
                                    <Tooltip content={t('common.edit') || ''} light>
                                        <Button
                                            to={`/goals/edit/${goal?._id}/`}
                                            title=""
                                            icon="settings"
                                            color="default"
                                        />
                                    </Tooltip>
                                </span>
                            </Ability>
                        </div>
                    </div>
                </header>
                {isLoading ? (
                    <main className="mx-6 mt-2 flex justify-center items-center h-[calc(100vh-8rem)] w-full">
                        <Icon type="loading" size={20} />
                    </main>
                ) : (
                    <main className="mx-6 mt-2">
                        <div className="mb-6">
                            <h3 className="text-center text-2xl font-bold print:text-left">
                                {goal.name}
                            </h3>
                            <h3 className="text-center text-xs italic font-normal print:hidden">
                                {t('goals.stopAt')}&nbsp;
                                {formatMomentDate(goal.stopAt, 'LL')}
                            </h3>
                        </div>

                        <div className="mb-6 print:hidden">
                            <hr />
                            <h4 className="mb-4 text-center font-light leading-7 text-gray-900 dark:text-gray-100">
                                {t('goals.projects')}&nbsp;:&nbsp;
                                {goal?.projects.map((p: Project) => p.name).join(', ')}
                            </h4>
                            <hr />
                            <h4 className="mb-4 text-center font-light leading-7 text-gray-900 dark:text-gray-100">
                                {t('goals.total_estimated', {total: goal.totalEstimated().toFixed(2)})}
                            </h4>
                            <hr />
                        </div>

                        <div className="border rounded p-2 pb-4 mb-4">
                            <h3 className="font-bold inline-flex items-center gap-1">
                                { t('goals.progression') }
                                <FieldHelp content={t('goals.help.progression') || ''} />
                            </h3>
                            <div className="flex items-center">
                                <div className="w-6">
                                    <Icon type="progression" />
                                </div>
                                <div className="w-full">
                                    { goalProgression(goal) }
                                </div>
                            </div>
                        </div>

                        <div className="border rounded p-2 pb-4 mb-4">
                            <h3 className="font-bold">{ t('goals.time_progression') }</h3>
                            <div className="flex items-center">
                                <div className="w-6">
                                    <Icon type="date" />
                                </div>
                                <div className="w-full">
                                    { timeProgression(goal) }
                                </div>
                            </div>
                        </div>

                        {(goal.milestones || []).length > 0 && (
                            <div className="border rounded p-2 pb-4 mb-4">
                                <h3 className="font-bold mb-3">{t('goals.milestones')}</h3>
                                <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-3 space-y-4">
                                    {[...(goal.milestones || [])]
                                        .sort((a, b) => a.at.valueOf() - b.at.valueOf())
                                        .map((milestone) => (
                                            <li key={milestone._id} className="ml-4">
                                                <div className="absolute w-3 h-3 bg-blue-500 rounded-full -left-1.5 border border-white dark:border-gray-800" />
                                                <time className="text-sm text-gray-500 dark:text-gray-400">
                                                    {formatMomentDate(milestone.at, 'LL')}
                                                </time>
                                                <h4 className="font-semibold text-gray-900 dark:text-gray-100">{milestone.name}</h4>
                                            </li>
                                        ))}
                                </ol>
                            </div>
                        )}

                        {(goal.phases || []).length > 0 && (
                            <div className="border rounded p-2 pb-4 mb-4 space-y-4">
                                <h3 className="font-bold">{t('goals.phases')}</h3>
                                {[...(goal.phases || [])]
                                    .sort((a, b) => {
                                        const aKey = (a.startAt || a.stopAt)?.valueOf() ?? Number.POSITIVE_INFINITY;
                                        const bKey = (b.startAt || b.stopAt)?.valueOf() ?? Number.POSITIVE_INFINITY;
                                        if (aKey !== bKey)
                                            return aKey - bKey;
                                        const aEnd = a.stopAt?.valueOf() ?? Number.POSITIVE_INFINITY;
                                        const bEnd = b.stopAt?.valueOf() ?? Number.POSITIVE_INFINITY;
                                        return aEnd - bEnd;
                                    })
                                    .map((phase) => {
                                    const phaseChanges = goal.changesForPhase(phase._id);
                                    return (
                                        <div key={phase._id} className="border rounded p-3">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                                <div>
                                                    <h4 className="font-semibold">{phase.name}</h4>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        {phase.startAt ? formatMomentDate(phase.startAt, 'LL') : '…'}
                                                        {' → '}
                                                        {phase.stopAt ? formatMomentDate(phase.stopAt, 'LL') : '…'}
                                                    </p>
                                                </div>
                                                <span className="text-sm text-gray-600 dark:text-gray-300">
                                                    {t('goals.total_estimated', { total: goal.totalEstimated(phaseChanges).toFixed(2) })}
                                                </span>
                                            </div>
                                            <div className="mb-3">
                                                {goalProgression(goal, phaseChanges)}
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                {phaseChanges.map((change) => (
                                                    <ChangeListItem key={change._id} change={change} showProject />
                                                ))}
                                                {!phaseChanges.length && (
                                                    <p className="text-sm text-gray-500 col-span-2">{t('goals.no_cards_in_phase')}</p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className="border rounded p-3 border-dashed">
                                    <h4 className="font-semibold mb-2">{t('goals.unphased')}</h4>
                                    <div className="mb-3">
                                        {goalProgression(goal, goal.changesForPhase(null))}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {goal.changesForPhase(null).map((change) => (
                                            <ChangeListItem key={change._id} change={change} showProject />
                                        ))}
                                        {!goal.changesForPhase(null).length && (
                                            <p className="text-sm text-gray-500 col-span-2">{t('goals.no_unphased_cards')}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {goal.autoAttachProjectChanges && (
                            <p className="mb-4 text-center text-sm italic text-gray-500 dark:text-gray-400">
                                {t('goals.auto_attach_active')}
                            </p>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-1 gap-2 mt-2 print:break-before-page">
                            <div className="col-span-1 border print:border-none p-4 print:p-0 rounded-md">
                                <Reporting
                                    goal={goal}
                                    changes={goal?.changes}
                                    tabs={['dashboard', 'changes', 'tracks']}
                                />
                            </div>
                        </div>
                    </main>
                )}
            </div>
        </Ability>
    );
}

export default GoalView;
